import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { insuranceClaimsClaimCoachStepStatusSchema, insuranceClaimsClaimCoachFactStateSchema, insuranceClaimsAobNoticeStatusSchema, insuranceClaimsAobNoticeTypeSchema, insuranceClaimsAdjusterSlaAttentionStateSchema, insuranceClaimsAdjusterSlaOverrideActionSchema, insuranceClaimsAdjusterSlaSeveritySchema, insuranceClaimsScopeCoveragePositionSchema, insuranceClaimsScopeCustomerModeSchema, insuranceClaimsScopeSourceTypeSchema, insuranceClaimsScopeStatusSchema, insuranceClaimsScopeTypeSchema, insuranceClaimsScopeWorkflowStageSchema, insuranceClaimsCarrierEstimateDecisionActionSchema, insuranceClaimsCarrierEstimateReviewStateSchema, insuranceClaimsCarrierEstimateSourceTypeSchema, insuranceClaimsCarrierEstimateSuggestionSchema, insuranceClaimsComplianceGuidanceModeSchema, insuranceClaimsComplianceReviewStateSchema, insuranceClaimsComplianceSourceAuthoritySchema, insuranceClaimsPaymentAdministrationStateSchema, insuranceClaimsPaymentEvidenceKindSchema, insuranceClaimsPaymentMilestoneKeySchema, insuranceClaimsPaymentMilestoneStatusSchema, insuranceClaimsNoticeDeliveryStateSchema, insuranceClaimsScopeOwnerUnavailableReasonSchema, insuranceClaimsClaimDeskAttentionReasonSchema, insuranceClaimsClaimDeskLaneSchema, } from '../contracts/index.js';
export const insuranceClaimsConfigurationValueSchema = z.strictObject({
    maximumClaims: z.union([z.literal(1_000), z.literal(5_000), z.literal(10_000), z.literal(25_000)]),
    maximumSupplementsPerClaim: z.number().int().min(1).max(500),
    maximumLinesPerSupplement: z.number().int().min(1).max(200),
    maximumEvidencePerSupplement: z.number().int().min(0).max(50),
    supplementNumberPrefix: z.string().regex(/^[A-Z][A-Z0-9-]{0,7}$/),
    defaultCurrencyCode: z.string().regex(/^[A-Z]{3}$/),
    adjusterResponseTargetHours: z.number().int().min(1).max(720),
});
export const insuranceClaimsConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.claims.configuration',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims behavior',
        helpText: 'Controls bounded Claims records, supplement limits, numbering, currency, and response targets.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            maximumClaims: 10_000,
            maximumSupplementsPerClaim: 100,
            maximumLinesPerSupplement: 200,
            maximumEvidencePerSupplement: 50,
            supplementNumberPrefix: 'SUP',
            defaultCurrencyCode: 'USD',
            adjusterResponseTargetHours: 48,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: insuranceClaimsConfigurationValueSchema,
});
export const insuranceClaimsDisplayValueSchema = z.strictObject({
    pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]),
    density: z.enum(['comfortable', 'compact']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    defaultView: z.enum(['desk', 'claims', 'supplements']),
    projectPanelEnabled: z.boolean(),
    showDefenseGroups: z.boolean(),
    showSupplementAging: z.boolean(),
    showInternalNotes: z.boolean(),
});
export const insuranceClaimsDisplaySetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.claims.display',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims display',
        helpText: 'Controls Claims density, card sizing, default workspace, Project panel, and ordinary detail visibility.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            pageSize: 50,
            density: 'comfortable',
            cardSize: 'standard',
            defaultView: 'desk',
            projectPanelEnabled: true,
            showDefenseGroups: true,
            showSupplementAging: true,
            showInternalNotes: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: insuranceClaimsDisplayValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => value,
        }],
});
const label = z.string().trim().min(1).max(180);
export const insuranceClaimsCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(300),
    claimsTabLabel: label,
    supplementsTabLabel: label,
    claimDeskTabLabel: label,
    claimDeskTitle: label,
    claimDeskDescription: z.string().trim().min(1).max(300),
    claimDeskVisibleClaimsLabel: label,
    claimDeskOpenClaimsLabel: label,
    claimDeskBlockedLabel: label,
    claimDeskActionDueLabel: label,
    claimDeskSupplementsLabel: label,
    claimDeskPendingSupplementsLabel: label,
    claimDeskCoachProgressLabel: label,
    claimDeskSupplementAgingLabel: label,
    claimDeskUnder48HoursLabel: label,
    claimDesk48To95HoursLabel: label,
    claimDesk96To167HoursLabel: label,
    claimDeskAtLeast168HoursLabel: label,
    claimDeskOpenClaimAction: label,
    claimDeskEmpty: z.string().trim().min(1).max(300),
    claimDeskClaimCoachUnavailable: z.string().trim().min(1).max(300),
    claimDeskSupplementsUnavailable: z.string().trim().min(1).max(300),
    claimDeskLaneLabels: z.record(insuranceClaimsClaimDeskLaneSchema, label),
    claimDeskAttentionReasonLabels: z.record(insuranceClaimsClaimDeskAttentionReasonSchema, label),
    projectPanelTitle: label,
    projectPanelEmpty: z.string().trim().min(1).max(300),
    createClaimAction: label,
    createSupplementAction: label,
    reviseAction: label,
    submitAction: label,
    approveAction: label,
    rejectAction: label,
    requestRevisionAction: label,
    paidAction: label,
    archiveAction: label,
    restoreAction: label,
    claimNumberLabel: label,
    policyNumberLabel: label,
    carrierLabel: label,
    adjusterLabel: label,
    projectLabel: label,
    tpaProgramLabel: label,
    perilTypeLabel: label,
    causeOfLossLabel: label,
    lossDateLabel: label,
    discoveryDateLabel: label,
    waterLossLabel: label,
    waterCategoryLabel: label,
    waterClassLabel: label,
    moldConditionLabel: label,
    statusLabel: label,
    deductibleLabel: label,
    notesLabel: label,
    reasonLabel: label,
    titleLabel: label,
    descriptionLabel: label,
    reasonCategoryLabel: label,
    evidenceLabel: label,
    lineDescriptionLabel: label,
    quantityLabel: label,
    unitLabel: label,
    unitPriceLabel: label,
    requestedAmountLabel: label,
    approvedAmountLabel: label,
    adjusterNotesLabel: label,
    rejectionReasonLabel: label,
    readinessLabel: label,
    readinessPartialLabel: z.string().trim().min(1).max(300),
    coveredLabel: label,
    necessaryLabel: label,
    doneEffectiveLabel: label,
    requestedLabel: label,
    approvedLabel: label,
    pendingLabel: label,
    responseTimeLabel: label,
    searchLabel: label,
    allStatusesLabel: label,
    includeArchivedLabel: label,
    loadingLabel: label,
    savingLabel: label,
    savedLabel: label,
    cancelAction: label,
    addLineAction: label,
    removeLineAction: label,
    closeAction: label,
    reopenAction: label,
    cancelClaimAction: label,
    permissionDenied: z.string().trim().min(1).max(240),
    loadError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
    emptyTitle: label,
    emptyDescription: z.string().trim().min(1).max(300),
    statusLabels: z.strictObject({
        open: label,
        closed: label,
        cancelled: label,
        draft: label,
        submitted: label,
        approved: label,
        rejected: label,
        revised: label,
        paid: label,
    }),
    perilLabels: z.strictObject({
        water: label,
        fire: label,
        mold: label,
        storm: label,
        biohazard: label,
        other: label,
    }),
    supplementReasonLabels: z.strictObject({
        hidden_damage: label,
        scope_change: label,
        code_upgrade: label,
        material_upgrade: label,
        mitigation_extension: label,
        other: label,
    }),
});
export const insuranceClaimsCopySetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.copy.workspace',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims interface copy',
        helpText: 'Controls ordinary tenant-visible Claims, supplement, readiness, filter, and Project-panel labels. Legal and consent warnings remain protected.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            navigationLabel: 'Insurance claims',
            title: 'Insurance claims',
            description: 'Track real claim facts, supplement decisions, and evidence-backed readiness without mixing owner data.',
            claimsTabLabel: 'Claims',
            supplementsTabLabel: 'Supplements',
            claimDeskTabLabel: 'Claims desk',
            claimDeskTitle: 'Insurance Claims desk',
            claimDeskDescription: 'Work the current Claims page by blocked, due, supplement, money, production, and closeout state.',
            claimDeskVisibleClaimsLabel: 'Visible claims',
            claimDeskOpenClaimsLabel: 'Open claims',
            claimDeskBlockedLabel: 'Blocked',
            claimDeskActionDueLabel: 'Action due',
            claimDeskSupplementsLabel: 'Supplements',
            claimDeskPendingSupplementsLabel: 'Pending supplements',
            claimDeskCoachProgressLabel: 'Claim Coach progress',
            claimDeskSupplementAgingLabel: 'Submitted supplement aging',
            claimDeskUnder48HoursLabel: 'Under 48 hours',
            claimDesk48To95HoursLabel: '48–95 hours',
            claimDesk96To167HoursLabel: '96–167 hours',
            claimDeskAtLeast168HoursLabel: '168+ hours',
            claimDeskOpenClaimAction: 'Open claim record',
            claimDeskEmpty: 'No insurance claims match this view.',
            claimDeskClaimCoachUnavailable: 'Claim Coach facts are unavailable for your current access.',
            claimDeskSupplementsUnavailable: 'Supplement facts are unavailable for your current access.',
            claimDeskLaneLabels: {
                blocked: 'Blocked / guardrails',
                action_due: 'Action due',
                intake: 'Intake / inspection',
                estimate_review: 'Estimate review',
                supplement: 'Supplement',
                money: 'Money / mortgage',
                production: 'Production handoff',
                closeout: 'Closeout',
            },
            claimDeskAttentionReasonLabels: {
                'claim-coach-blocked': 'Claim Coach blocked',
                'claim-coach-overdue': 'Claim Coach overdue',
                'supplement-response-critical': 'Supplement response at 168+ hours',
                'supplement-response-high': 'Supplement response at 96–167 hours',
                'supplement-response-medium': 'Supplement response at 48–95 hours',
                'supplement-revision-needed': 'Supplement revision needed',
                'approved-supplement-unpaid': 'Approved supplement not marked paid',
            },
            projectPanelTitle: 'Insurance claim',
            projectPanelEmpty: 'No insurance claim is linked to this Project.',
            createClaimAction: 'Create claim',
            createSupplementAction: 'Create supplement',
            reviseAction: 'Save revision',
            submitAction: 'Submit',
            approveAction: 'Approve',
            rejectAction: 'Reject',
            requestRevisionAction: 'Request revision',
            paidAction: 'Acknowledge paid',
            archiveAction: 'Archive',
            restoreAction: 'Restore',
            claimNumberLabel: 'Claim number',
            policyNumberLabel: 'Policy number',
            carrierLabel: 'Carrier',
            adjusterLabel: 'Adjuster',
            projectLabel: 'Project',
            tpaProgramLabel: 'TPA program',
            perilTypeLabel: 'Peril',
            causeOfLossLabel: 'Cause of loss',
            lossDateLabel: 'Date of loss',
            discoveryDateLabel: 'Date discovered',
            waterLossLabel: 'Water loss',
            waterCategoryLabel: 'Water Category',
            waterClassLabel: 'Water Class',
            moldConditionLabel: 'Mold Condition',
            statusLabel: 'Status',
            deductibleLabel: 'Deductible',
            notesLabel: 'Internal notes',
            reasonLabel: 'Reason for change',
            titleLabel: 'Title',
            descriptionLabel: 'Description',
            reasonCategoryLabel: 'Supplement reason',
            evidenceLabel: 'Evidence',
            lineDescriptionLabel: 'Line description',
            quantityLabel: 'Quantity',
            unitLabel: 'Unit',
            unitPriceLabel: 'Unit price',
            requestedAmountLabel: 'Requested',
            approvedAmountLabel: 'Approved',
            adjusterNotesLabel: 'Adjuster notes',
            rejectionReasonLabel: 'Rejection reason',
            readinessLabel: 'Claim readiness',
            readinessPartialLabel: 'Readiness is partial until required owner facts are available.',
            coveredLabel: 'Covered',
            necessaryLabel: 'Necessary',
            doneEffectiveLabel: 'Done & Effective',
            requestedLabel: 'Requested',
            approvedLabel: 'Approved',
            pendingLabel: 'Pending',
            responseTimeLabel: 'Average response',
            searchLabel: 'Search claims',
            allStatusesLabel: 'All statuses',
            includeArchivedLabel: 'Include archived',
            loadingLabel: 'Loading Insurance Claims…',
            savingLabel: 'Saving…',
            savedLabel: 'Saved',
            cancelAction: 'Cancel',
            addLineAction: 'Add line',
            removeLineAction: 'Remove line',
            closeAction: 'Close claim',
            reopenAction: 'Reopen claim',
            cancelClaimAction: 'Cancel claim',
            permissionDenied: 'You do not have permission to make that Insurance Claims change.',
            loadError: 'Insurance Claims could not be loaded.',
            mutationError: 'The Insurance Claims change could not be saved. Refresh and try again.',
            emptyTitle: 'No insurance claims found',
            emptyDescription: 'No insurance claims match the current filters.',
            statusLabels: {
                open: 'Open',
                closed: 'Closed',
                cancelled: 'Cancelled',
                draft: 'Draft',
                submitted: 'Submitted',
                approved: 'Approved',
                rejected: 'Rejected',
                revised: 'Revision requested',
                paid: 'Paid',
            },
            perilLabels: {
                water: 'Water',
                fire: 'Fire',
                mold: 'Mold',
                storm: 'Storm',
                biohazard: 'Biohazard',
                other: 'Other',
            },
            supplementReasonLabels: {
                hidden_damage: 'Hidden damage',
                scope_change: 'Scope change',
                code_upgrade: 'Code upgrade',
                material_upgrade: 'Material upgrade',
                mitigation_extension: 'Mitigation extension',
                other: 'Other',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: insuranceClaimsCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
                ? {
                    ...value,
                    claimDeskTabLabel: 'Claims desk',
                    claimDeskTitle: 'Insurance Claims desk',
                    claimDeskDescription: 'Work the current Claims page by blocked, due, supplement, money, production, and closeout state.',
                    claimDeskVisibleClaimsLabel: 'Visible claims',
                    claimDeskOpenClaimsLabel: 'Open claims',
                    claimDeskBlockedLabel: 'Blocked',
                    claimDeskActionDueLabel: 'Action due',
                    claimDeskSupplementsLabel: 'Supplements',
                    claimDeskPendingSupplementsLabel: 'Pending supplements',
                    claimDeskCoachProgressLabel: 'Claim Coach progress',
                    claimDeskSupplementAgingLabel: 'Submitted supplement aging',
                    claimDeskUnder48HoursLabel: 'Under 48 hours',
                    claimDesk48To95HoursLabel: '48–95 hours',
                    claimDesk96To167HoursLabel: '96–167 hours',
                    claimDeskAtLeast168HoursLabel: '168+ hours',
                    claimDeskOpenClaimAction: 'Open claim record',
                    claimDeskEmpty: 'No insurance claims match this view.',
                    claimDeskClaimCoachUnavailable: 'Claim Coach facts are unavailable for your current access.',
                    claimDeskSupplementsUnavailable: 'Supplement facts are unavailable for your current access.',
                    claimDeskLaneLabels: {
                        blocked: 'Blocked / guardrails', action_due: 'Action due',
                        intake: 'Intake / inspection', estimate_review: 'Estimate review',
                        supplement: 'Supplement', money: 'Money / mortgage',
                        production: 'Production handoff', closeout: 'Closeout',
                    },
                    claimDeskAttentionReasonLabels: {
                        'claim-coach-blocked': 'Claim Coach blocked',
                        'claim-coach-overdue': 'Claim Coach overdue',
                        'supplement-response-critical': 'Supplement response at 168+ hours',
                        'supplement-response-high': 'Supplement response at 96–167 hours',
                        'supplement-response-medium': 'Supplement response at 48–95 hours',
                        'supplement-revision-needed': 'Supplement revision needed',
                        'approved-supplement-unpaid': 'Approved supplement not marked paid',
                    },
                }
                : value,
        }],
});
export const insuranceClaimsWorkflowConfigurationValueSchema = z.strictObject({
    maximumScopesPerClaim: z.number().int().min(1).max(250),
    allowReasonedScopeBypass: z.boolean(),
    estimateAllocationToleranceCents: z.number().int().min(0).max(100_000),
    coachDueWarningDays: z.number().int().min(0).max(90),
    maximumEvidencePerCoachStep: z.number().int().min(0).max(20),
});
export const insuranceClaimsWorkflowConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.workflow.configuration',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims workflow behavior',
        helpText: 'Controls scope limits, reasoned bypass, estimate allocation tolerance, and Claim Coach evidence limits.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            maximumScopesPerClaim: 100,
            allowReasonedScopeBypass: true,
            estimateAllocationToleranceCents: 0,
            coachDueWarningDays: 3,
            maximumEvidencePerCoachStep: 20,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: insuranceClaimsWorkflowConfigurationValueSchema,
});
export const insuranceClaimsWorkflowDisplayValueSchema = z.strictObject({
    defaultWorkflowView: z.enum(['scopes', 'claim-coach']),
    scopeCardSize: z.enum(['compact', 'standard', 'wide']),
    showEstimateSplitAudit: z.boolean(),
    showCoveragePosition: z.boolean(),
    showCoachEvidenceFacts: z.boolean(),
    showCoachEvidenceSummary: z.boolean(),
    showScopeOwnerEvidence: z.boolean(),
    collapseCompletedCoachSteps: z.boolean(),
    projectWorkflowPanelEnabled: z.boolean(),
});
export const insuranceClaimsWorkflowDisplaySetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.workflow.display',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims workflow display',
        helpText: 'Controls scope cards, estimate reconciliation, and Claim Coach presentation.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 3,
        codedFallback: {
            defaultWorkflowView: 'scopes',
            scopeCardSize: 'standard',
            showEstimateSplitAudit: true,
            showCoveragePosition: true,
            showCoachEvidenceFacts: true,
            showCoachEvidenceSummary: true,
            showScopeOwnerEvidence: true,
            collapseCompletedCoachSteps: false,
            projectWorkflowPanelEnabled: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: insuranceClaimsWorkflowDisplayValueSchema,
    migrations: [
        {
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
                ? { ...value, showCoachEvidenceSummary: true }
                : value,
        },
        {
            fromVersion: 2,
            toVersion: 3,
            migrate: (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
                ? { ...value, showScopeOwnerEvidence: true }
                : value,
        },
    ],
});
export const insuranceClaimsWorkflowCopyValueSchema = z.strictObject({
    scopesTabLabel: label,
    claimCoachTabLabel: label,
    createScopeAction: label,
    reviseScopeAction: label,
    reviewReadyAction: label,
    confirmScopeAction: label,
    bypassScopeAction: label,
    estimateSplitTitle: label,
    estimateProviderUnavailable: z.string().trim().min(1).max(300),
    estimateNotConfigured: z.string().trim().min(1).max(300),
    claimCoachTitle: label,
    claimCoachUnavailableFacts: z.string().trim().min(1).max(300),
    scopeNameLabel: label,
    scopeTypeLabel: label,
    scopeSourceLabel: label,
    customerScopeModeLabel: label,
    customerLimitationsLabel: label,
    coveragePositionLabel: label,
    allocationLabel: label,
    workflowStageLabel: label,
    dueDateLabel: label,
    assigneeLabel: label,
    blockedExplanationLabel: label,
    claimLabel: label,
    statusLabel: label,
    descriptionLabel: label,
    coverageNotesLabel: label,
    reasonLabel: label,
    ownerFactsLabel: label,
    ownerFactsSummaryLabel: label,
    ownerFactsTotalLabel: label,
    ownerFactsAttentionLabel: label,
    ownerFactsReadyLabel: label,
    scopeOwnerEvidenceLabel: label,
    scopeCompletionDocumentsLabel: label,
    scopeInvoicePaymentLabel: label,
    scopeDocumentCountLabel: label,
    scopeSignedCountLabel: label,
    scopeFinalizedCountLabel: label,
    scopeInvoiceCountLabel: label,
    scopeBilledLabel: label,
    scopePaidLabel: label,
    scopeWrittenOffLabel: label,
    scopeDueLabel: label,
    scopeOwnerEvidenceUnavailable: z.string().trim().min(1).max(300),
    scopeOwnerEvidenceLoadError: z.string().trim().min(1).max(300),
    scopeOwnerUnavailableReasonLabels: z.record(insuranceClaimsScopeOwnerUnavailableReasonSchema, z.string().trim().min(1).max(300)),
    notesAction: label,
    stepNotesLabel: label,
    completeAction: label,
    archiveAction: label,
    restoreAction: label,
    cancelAction: label,
    loadingLabel: label,
    savedLabel: label,
    noProjectClaim: z.string().trim().min(1).max(300),
    scopeLoadError: z.string().trim().min(1).max(300),
    scopeMutationError: z.string().trim().min(1).max(300),
    scopePermissionDenied: z.string().trim().min(1).max(300),
    createScopeReason: z.string().trim().min(10).max(500),
    reviseScopeReason: z.string().trim().min(10).max(500),
    claimCoachLoadError: z.string().trim().min(1).max(300),
    claimCoachMutationError: z.string().trim().min(1).max(300),
    estimateAllocationStatusPrefix: label,
    coachProgressConnector: z.string().trim().min(1).max(80),
    scopeTypeLabels: z.record(insuranceClaimsScopeTypeSchema, label),
    scopeSourceLabels: z.record(insuranceClaimsScopeSourceTypeSchema, label),
    customerScopeModeLabels: z.record(insuranceClaimsScopeCustomerModeSchema, label),
    coveragePositionLabels: z.record(insuranceClaimsScopeCoveragePositionSchema, label),
    scopeStatusLabels: z.record(insuranceClaimsScopeStatusSchema, label),
    workflowStageLabels: z.record(insuranceClaimsScopeWorkflowStageSchema, label),
    claimCoachStatusLabels: z.record(insuranceClaimsClaimCoachStepStatusSchema, label),
    claimCoachFactStateLabels: z.record(insuranceClaimsClaimCoachFactStateSchema, label),
    claimCoachTextOverrides: z.record(z.string().regex(/^(?:step|checklist|fact)\.[a-z0-9_-]+\.[a-z0-9_-]+$/), label),
});
export const insuranceClaimsWorkflowCopySetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.copy.workflow',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims workflow copy',
        helpText: 'Controls ordinary tenant-visible scope, estimate split, and Claim Coach labels. Decision and compliance requirements remain protected.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 3,
        codedFallback: {
            scopesTabLabel: 'Scopes',
            claimCoachTabLabel: 'Claim Coach',
            createScopeAction: 'Create scope',
            reviseScopeAction: 'Save scope revision',
            reviewReadyAction: 'Mark review ready',
            confirmScopeAction: 'Confirm scope',
            bypassScopeAction: 'Bypass with explanation',
            estimateSplitTitle: 'Estimate split',
            estimateProviderUnavailable: 'Estimate verification is unavailable because Estimating is not active.',
            estimateNotConfigured: 'No scope has been allocated to the current estimate.',
            claimCoachTitle: 'Insurance Claim Coach',
            claimCoachUnavailableFacts: 'Owner facts are unavailable; manual workflow progress remains separate.',
            scopeNameLabel: 'Scope name',
            scopeTypeLabel: 'Scope type',
            scopeSourceLabel: 'Scope source',
            customerScopeModeLabel: 'Customer scope',
            customerLimitationsLabel: 'Customer limitations',
            coveragePositionLabel: 'Coverage position',
            allocationLabel: 'Estimate allocation',
            workflowStageLabel: 'Workflow stage',
            dueDateLabel: 'Due date',
            assigneeLabel: 'Assignee',
            blockedExplanationLabel: 'Blocked or skipped explanation',
            claimLabel: 'Claim',
            statusLabel: 'Status',
            descriptionLabel: 'Description',
            coverageNotesLabel: 'Coverage notes',
            reasonLabel: 'Reason',
            ownerFactsLabel: 'Owner facts',
            ownerFactsSummaryLabel: 'Owner evidence summary',
            ownerFactsTotalLabel: 'Total facts',
            ownerFactsAttentionLabel: 'steps need evidence attention',
            ownerFactsReadyLabel: 'No owner evidence gaps are currently reported.',
            scopeOwnerEvidenceLabel: 'Scope completion and billing',
            scopeCompletionDocumentsLabel: 'Completion documents',
            scopeInvoicePaymentLabel: 'Invoices and payments',
            scopeDocumentCountLabel: 'Documents',
            scopeSignedCountLabel: 'Signed',
            scopeFinalizedCountLabel: 'Finalized',
            scopeInvoiceCountLabel: 'Invoices',
            scopeBilledLabel: 'Billed',
            scopePaidLabel: 'Paid',
            scopeWrittenOffLabel: 'Written off',
            scopeDueLabel: 'Due',
            scopeOwnerEvidenceUnavailable: 'This owner module is unavailable for the current tenant or user.',
            scopeOwnerEvidenceLoadError: 'Scope completion and billing evidence could not be loaded.',
            scopeOwnerUnavailableReasonLabels: {
                'not-installed': 'The owner module is not installed.',
                'not-enabled': 'The owner module is not enabled for this tenant.',
                'permission-denied': 'You do not have permission to view these owner facts.',
                'provider-invalid': 'The owner module returned facts for a different record.',
                'provider-failed': 'The owner module could not return its facts.',
            },
            notesAction: 'Notes',
            stepNotesLabel: 'Step notes',
            completeAction: 'Complete',
            archiveAction: 'Archive',
            restoreAction: 'Restore',
            cancelAction: 'Cancel',
            loadingLabel: 'Loading claim workflow…',
            savedLabel: 'Scope saved.',
            noProjectClaim: 'No insurance claim is linked to this Project.',
            scopeLoadError: 'Insurance Claims scopes could not be loaded.',
            scopeMutationError: 'The claim scope workflow change could not be saved.',
            scopePermissionDenied: 'You do not have permission to change claim scopes.',
            createScopeReason: 'Create this Insurance Claims scope',
            reviseScopeReason: 'Revise this Insurance Claims scope',
            claimCoachLoadError: 'Insurance Claim Coach could not be loaded.',
            claimCoachMutationError: 'The Claim Coach step could not be saved.',
            estimateAllocationStatusPrefix: 'Estimate allocation status',
            coachProgressConnector: 'of 10 complete',
            scopeTypeLabels: {
                mitigation: 'Mitigation', reconstruction: 'Reconstruction', mold: 'Mold',
                contents: 'Contents', cleaning: 'Cleaning', other: 'Other',
            },
            scopeSourceLabels: {
                typed: 'Typed', video: 'Video', intake: 'Intake', manual: 'Manual',
                assistant: 'Assistant', import: 'Import',
            },
            customerScopeModeLabels: {
                standard: 'Standard', customer_limited: 'Customer limited',
            },
            coveragePositionLabels: {
                unknown: 'Unknown', covered: 'Covered', partially_covered: 'Partially covered',
                not_covered: 'Not covered', disputed: 'Disputed',
            },
            scopeStatusLabels: {
                active: 'Active', completed: 'Completed', on_hold: 'On hold',
                disputed: 'Disputed', cancelled: 'Cancelled',
            },
            workflowStageLabels: {
                draft: 'Draft', review_ready: 'Review ready', confirmed: 'Confirmed', bypassed: 'Bypassed',
            },
            claimCoachStatusLabels: {
                not_started: 'Not started', in_progress: 'In progress', blocked: 'Blocked',
                complete: 'Complete', skipped: 'Skipped',
            },
            claimCoachFactStateLabels: {
                present: 'Present', missing: 'Missing', unavailable: 'Unavailable',
                not_required: 'Not required',
            },
            claimCoachTextOverrides: {},
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: insuranceClaimsWorkflowCopyValueSchema,
    migrations: [
        {
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
                ? {
                    ...value,
                    ownerFactsSummaryLabel: 'Owner evidence summary',
                    ownerFactsTotalLabel: 'Total facts',
                    ownerFactsAttentionLabel: 'steps need evidence attention',
                    ownerFactsReadyLabel: 'No owner evidence gaps are currently reported.',
                }
                : value,
        },
        {
            fromVersion: 2,
            toVersion: 3,
            migrate: (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
                ? {
                    ...value,
                    scopeOwnerEvidenceLabel: 'Scope completion and billing',
                    scopeCompletionDocumentsLabel: 'Completion documents',
                    scopeInvoicePaymentLabel: 'Invoices and payments',
                    scopeDocumentCountLabel: 'Documents',
                    scopeSignedCountLabel: 'Signed',
                    scopeFinalizedCountLabel: 'Finalized',
                    scopeInvoiceCountLabel: 'Invoices',
                    scopeBilledLabel: 'Billed',
                    scopePaidLabel: 'Paid',
                    scopeWrittenOffLabel: 'Written off',
                    scopeDueLabel: 'Due',
                    scopeOwnerEvidenceUnavailable: 'This owner module is unavailable for the current tenant or user.',
                    scopeOwnerEvidenceLoadError: 'Scope completion and billing evidence could not be loaded.',
                    scopeOwnerUnavailableReasonLabels: {
                        'not-installed': 'The owner module is not installed.',
                        'not-enabled': 'The owner module is not enabled for this tenant.',
                        'permission-denied': 'You do not have permission to view these owner facts.',
                        'provider-invalid': 'The owner module returned facts for a different record.',
                        'provider-failed': 'The owner module could not return its facts.',
                    },
                }
                : value,
        },
    ],
});
export const insuranceClaimsNoticeSlaConfigurationValueSchema = z.strictObject({
    noticeDueBusinessDays: z.number().int().min(1).max(30),
    maximumActiveNoticesPerClaim: z.number().int().min(1).max(100),
    allowManualNotices: z.boolean(),
    allowOperatorAttestedArtifact: z.boolean(),
    allowOperatorAttestedPhysicalDelivery: z.boolean(),
    adjusterResponseTargetHours: z.number().int().min(1).max(720),
    adjusterResponseHighHours: z.number().int().min(2).max(720),
    adjusterResponseCriticalHours: z.number().int().min(3).max(720),
    defaultSnoozeHours: z.number().int().min(1).max(168),
    maximumSlaNotesLength: z.number().int().min(100).max(1_000),
}).superRefine((value, context) => {
    if (!(value.adjusterResponseTargetHours < value.adjusterResponseHighHours &&
        value.adjusterResponseHighHours < value.adjusterResponseCriticalHours)) {
        context.addIssue({
            code: 'custom',
            path: ['adjusterResponseTargetHours'],
            message: 'Adjuster SLA target, high, and critical thresholds must increase strictly.',
        });
    }
});
export const insuranceClaimsNoticeSlaConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.notice-sla.configuration',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims notice and SLA behavior',
        helpText: 'Controls protected notice timing, bounded manual evidence, and adjuster response thresholds without embedding legal prose or provider actions.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            noticeDueBusinessDays: 5,
            maximumActiveNoticesPerClaim: 20,
            allowManualNotices: true,
            allowOperatorAttestedArtifact: true,
            allowOperatorAttestedPhysicalDelivery: true,
            adjusterResponseTargetHours: 48,
            adjusterResponseHighHours: 96,
            adjusterResponseCriticalHours: 168,
            defaultSnoozeHours: 24,
            maximumSlaNotesLength: 1_000,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: insuranceClaimsNoticeSlaConfigurationValueSchema,
});
export const insuranceClaimsNoticeSlaDisplayValueSchema = z.strictObject({
    defaultView: z.enum(['notices', 'adjuster-sla']),
    noticeCardSize: z.enum(['compact', 'standard', 'wide']),
    showAcknowledgedNotices: z.boolean(),
    showCancelledNotices: z.boolean(),
    showWaitingOnAdjuster: z.boolean(),
    compactSlaSummary: z.boolean(),
    projectNoticeSlaPanelEnabled: z.boolean(),
    showNoticeOwnerEvidence: z.boolean(),
});
export const insuranceClaimsNoticeSlaDisplaySetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.notice-sla.display',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims notice and SLA display',
        helpText: 'Controls notice card sizing, completed visibility, SLA presentation, and Project panel composition.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            defaultView: 'notices',
            noticeCardSize: 'standard',
            showAcknowledgedNotices: false,
            showCancelledNotices: false,
            showWaitingOnAdjuster: true,
            compactSlaSummary: false,
            projectNoticeSlaPanelEnabled: true,
            showNoticeOwnerEvidence: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: insuranceClaimsNoticeSlaDisplayValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
                ? { ...value, showNoticeOwnerEvidence: true }
                : value,
        }],
});
export const insuranceClaimsNoticeSlaCopyValueSchema = z.strictObject({
    noticeSlaTabLabel: label,
    noticesTabLabel: label,
    adjusterSlaTabLabel: label,
    noticeTitle: label,
    adjusterSlaTitle: label,
    createNoticeAction: label,
    reviseNoticeAction: label,
    cancelNoticeAction: label,
    acknowledgeNoticeAction: label,
    noticeTypeLabel: label,
    noticeStatusLabel: label,
    dueAtLabel: label,
    recipientLabel: label,
    insuredLabel: label,
    authorizationSourceLabel: label,
    artifactLabel: label,
    deliveryLabel: label,
    signedDocumentsLabel: label,
    finalizedArtifactsLabel: label,
    latestDeliveryLabel: label,
    reasonLabel: label,
    overdueLabel: label,
    activeNoticesLabel: label,
    slaTargetLabel: label,
    waitingOnTeamLabel: label,
    waitingOnAdjusterLabel: label,
    snoozeAction: label,
    acknowledgeSlaAction: label,
    escalateAction: label,
    clearOverrideAction: label,
    notesLabel: label,
    loadingLabel: label,
    noNotices: z.string().trim().min(1).max(300),
    ownerEvidenceUnavailable: z.string().trim().min(1).max(300),
    noLinkedArtifactEvidence: z.string().trim().min(1).max(300),
    noLinkedDeliveryEvidence: z.string().trim().min(1).max(300),
    slaProviderUnavailable: z.string().trim().min(1).max(300),
    noticeLoadError: z.string().trim().min(1).max(300),
    noticeMutationError: z.string().trim().min(1).max(300),
    slaLoadError: z.string().trim().min(1).max(300),
    slaMutationError: z.string().trim().min(1).max(300),
    noProjectClaim: z.string().trim().min(1).max(300),
    createNoticeReason: z.string().trim().min(10).max(500),
    noticeTypeLabels: z.record(insuranceClaimsAobNoticeTypeSchema, label),
    noticeStatusLabels: z.record(insuranceClaimsAobNoticeStatusSchema, label),
    deliveryStateLabels: z.record(insuranceClaimsNoticeDeliveryStateSchema, label),
    slaSeverityLabels: z.record(insuranceClaimsAdjusterSlaSeveritySchema, label),
    slaAttentionLabels: z.record(insuranceClaimsAdjusterSlaAttentionStateSchema, label),
    slaOverrideLabels: z.record(insuranceClaimsAdjusterSlaOverrideActionSchema, label),
});
export const insuranceClaimsNoticeSlaCopySetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.copy.notice-sla',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims notice and SLA interface copy',
        helpText: 'Controls ordinary notice and SLA interface labels. Legal clauses, consent language, and protected compliance guidance are not ordinary copy.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            noticeSlaTabLabel: 'Notices & SLA',
            noticesTabLabel: 'AOB notices',
            adjusterSlaTabLabel: 'Adjuster response SLA',
            noticeTitle: 'AOB / direct-pay notices',
            adjusterSlaTitle: 'Adjuster response SLA',
            createNoticeAction: 'Create notice',
            reviseNoticeAction: 'Save notice revision',
            cancelNoticeAction: 'Cancel notice',
            acknowledgeNoticeAction: 'Acknowledge receipt',
            noticeTypeLabel: 'Notice type',
            noticeStatusLabel: 'Notice status',
            dueAtLabel: 'Due',
            recipientLabel: 'Carrier / adjuster',
            insuredLabel: 'Insured',
            authorizationSourceLabel: 'Authorization source',
            artifactLabel: 'Final notice artifact',
            deliveryLabel: 'Delivery evidence',
            signedDocumentsLabel: 'Signed documents',
            finalizedArtifactsLabel: 'Finalized artifacts',
            latestDeliveryLabel: 'Latest delivery state',
            reasonLabel: 'Reason',
            overdueLabel: 'Overdue',
            activeNoticesLabel: 'Active notices',
            slaTargetLabel: 'Response target',
            waitingOnTeamLabel: 'Waiting on our team',
            waitingOnAdjusterLabel: 'Waiting on adjuster',
            snoozeAction: 'Snooze',
            acknowledgeSlaAction: 'Acknowledge',
            escalateAction: 'Escalate',
            clearOverrideAction: 'Clear override',
            notesLabel: 'Notes',
            loadingLabel: 'Loading notices and SLA…',
            noNotices: 'No AOB or direct-pay notices are recorded for this claim.',
            ownerEvidenceUnavailable: 'Linked owner evidence is currently unavailable.',
            noLinkedArtifactEvidence: 'No exact notice-linked document artifact is recorded.',
            noLinkedDeliveryEvidence: 'No exact notice-linked delivery evidence is recorded.',
            slaProviderUnavailable: 'Adjuster SLA facts are unavailable because the Communications fact provider is not active.',
            noticeLoadError: 'Insurance Claims notices could not be loaded.',
            noticeMutationError: 'The notice change could not be saved.',
            slaLoadError: 'Adjuster SLA facts could not be loaded.',
            slaMutationError: 'The adjuster SLA decision could not be saved.',
            noProjectClaim: 'No insurance claim is linked to this Project.',
            createNoticeReason: 'Create this Insurance Claims AOB notice',
            noticeTypeLabels: {
                aob: 'Assignment of Benefits',
                direct_pay: 'Direct pay',
                aif: 'Authorization in fact',
                combined: 'AOB / direct pay / AIF',
            },
            noticeStatusLabels: {
                pending: 'Pending', generated: 'Generated', sent: 'Sent',
                acknowledged: 'Acknowledged', cancelled: 'Cancelled',
            },
            deliveryStateLabels: {
                queued: 'Queued', sent: 'Sent', delivered: 'Delivered', failed: 'Failed',
                suppressed: 'Suppressed',
            },
            slaSeverityLabels: {
                normal: 'Within target', medium: 'Medium', high: 'High', critical: 'Critical',
            },
            slaAttentionLabels: {
                'waiting-on-team': 'Waiting on our team',
                'waiting-on-adjuster': 'Waiting on adjuster',
            },
            slaOverrideLabels: {
                snoozed: 'Snoozed', acknowledged: 'Acknowledged',
                escalated: 'Escalated', cleared: 'Cleared',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: insuranceClaimsNoticeSlaCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
                ? {
                    ...value,
                    signedDocumentsLabel: 'Signed documents',
                    finalizedArtifactsLabel: 'Finalized artifacts',
                    latestDeliveryLabel: 'Latest delivery state',
                    ownerEvidenceUnavailable: 'Linked owner evidence is currently unavailable.',
                    noLinkedArtifactEvidence: 'No exact notice-linked document artifact is recorded.',
                    noLinkedDeliveryEvidence: 'No exact notice-linked delivery evidence is recorded.',
                    deliveryStateLabels: {
                        queued: 'Queued', sent: 'Sent', delivered: 'Delivered', failed: 'Failed',
                        suppressed: 'Suppressed',
                    },
                }
                : value,
        }],
});
export const insuranceClaimsCarrierReviewConfigurationValueSchema = z.strictObject({
    maximumCarrierLines: z.number().int().min(1).max(500),
    minimumMatchBasisPoints: z.number().int().min(0).max(10_000),
    quantityVarianceBasisPoints: z.number().int().min(0).max(10_000),
    quantityVarianceMinimumTenThousandths: z.number().int().min(0).max(1_000_000),
});
export const insuranceClaimsCarrierReviewConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.carrier-review.configuration',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims carrier review behavior',
        helpText: 'Controls bounded carrier imports and deterministic comparison suggestions. Operator decisions remain explicit.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            maximumCarrierLines: 500,
            minimumMatchBasisPoints: 3_500,
            quantityVarianceBasisPoints: 800,
            quantityVarianceMinimumTenThousandths: 10_000,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'protected',
    },
    valueSchema: insuranceClaimsCarrierReviewConfigurationValueSchema,
});
export const insuranceClaimsCarrierReviewDisplayValueSchema = z.strictObject({
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showMatchScores: z.boolean(),
    showAlignedLines: z.boolean(),
    showPreparedReviews: z.boolean(),
    projectCarrierReviewPanelEnabled: z.boolean(),
});
export const insuranceClaimsCarrierReviewDisplaySetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.carrier-review.display',
        moduleId: 'insurance-claims', title: 'Insurance Claims carrier review display',
        helpText: 'Controls carrier review cards, comparison details, and Project composition.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            cardSize: 'standard', showMatchScores: true, showAlignedLines: true,
            showPreparedReviews: true, projectCarrierReviewPanelEnabled: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: insuranceClaimsCarrierReviewDisplayValueSchema,
});
export const insuranceClaimsCarrierReviewCopyValueSchema = z.strictObject({
    carrierReviewTabLabel: label,
    title: label,
    description: z.string().trim().min(1).max(300),
    importAction: label,
    compareAction: label,
    prepareAction: label,
    saveDecisionAction: label,
    claimLabel: label,
    sourceTypeLabel: label,
    carrierLabel: label,
    claimNumberLabel: label,
    estimateNumberLabel: label,
    estimateDateLabel: label,
    sourceFileLabel: label,
    csvLabel: label,
    reasonLabel: label,
    statusLabel: label,
    carrierTotalLabel: label,
    currentTotalLabel: label,
    candidatesLabel: label,
    requestedAmountLabel: label,
    suggestionLabel: label,
    decisionLabel: label,
    coverageLabel: label,
    decisionNoteLabel: label,
    matchScoreLabel: label,
    providerUnavailable: z.string().trim().min(1).max(300),
    noReviews: z.string().trim().min(1).max(300),
    loadingLabel: label,
    loadError: z.string().trim().min(1).max(300),
    mutationError: z.string().trim().min(1).max(300),
    pendingDecisionsError: z.string().trim().min(1).max(300),
    createReason: z.string().trim().min(10).max(500),
    compareReason: z.string().trim().min(10).max(500),
    prepareReason: z.string().trim().min(10).max(500),
    sourceTypeLabels: z.record(insuranceClaimsCarrierEstimateSourceTypeSchema, label),
    reviewStateLabels: z.record(insuranceClaimsCarrierEstimateReviewStateSchema, label),
    suggestionLabels: z.record(insuranceClaimsCarrierEstimateSuggestionSchema, label),
    decisionLabels: z.record(insuranceClaimsCarrierEstimateDecisionActionSchema, label),
    coverageLabels: z.record(insuranceClaimsScopeCoveragePositionSchema, label),
});
export const insuranceClaimsCarrierReviewCopySetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.copy.carrier-review', moduleId: 'insurance-claims',
        title: 'Insurance Claims carrier review copy',
        helpText: 'Controls ordinary carrier review, comparison, and candidate-preparation labels.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            carrierReviewTabLabel: 'Carrier estimate review',
            title: 'Carrier estimate review',
            description: 'Compare a carrier estimate with exact Estimating facts, decide each variance, and prepare candidates without creating or sending a supplement.',
            importAction: 'Import carrier estimate', compareAction: 'Compare current estimate',
            prepareAction: 'Prepare supplement candidates', saveDecisionAction: 'Save decision',
            claimLabel: 'Claim', sourceTypeLabel: 'Source type', carrierLabel: 'Carrier',
            claimNumberLabel: 'Claim number', estimateNumberLabel: 'Estimate number',
            estimateDateLabel: 'Estimate date', sourceFileLabel: 'Source file name',
            csvLabel: 'Carrier / Xactimate CSV', reasonLabel: 'Reason', statusLabel: 'Status',
            carrierTotalLabel: 'Carrier RCV', currentTotalLabel: 'Current estimate',
            candidatesLabel: 'Included candidates', requestedAmountLabel: 'Prepared amount',
            suggestionLabel: 'Comparison suggestion', decisionLabel: 'Operator decision',
            coverageLabel: 'Coverage position', decisionNoteLabel: 'Decision explanation',
            matchScoreLabel: 'Match confidence',
            providerUnavailable: 'Current Estimating facts are unavailable. The carrier snapshot remains intact and no comparison was fabricated.',
            noReviews: 'No carrier estimate review has been imported for this claim.',
            loadingLabel: 'Loading carrier reviews…',
            loadError: 'Carrier estimate reviews could not be loaded.',
            mutationError: 'The carrier estimate review change could not be saved.',
            pendingDecisionsError: 'Resolve every actionable comparison before preparing candidates.',
            createReason: 'Import this carrier estimate snapshot for review',
            compareReason: 'Compare this carrier snapshot with the current estimate',
            prepareReason: 'Prepare the explicitly included supplement candidates',
            sourceTypeLabels: {
                carrier_csv: 'Carrier CSV', xactimate_csv: 'Xactimate CSV',
                xactimate_pdf: 'Xactimate PDF', scope_pdf: 'Scope PDF', manual: 'Manual', other: 'Other',
            },
            reviewStateLabels: { imported: 'Imported', reviewing: 'Reviewing', prepared: 'Prepared' },
            suggestionLabels: {
                uncompared: 'Not compared', aligned: 'Aligned', 'quantity-review': 'Quantity review',
                'carrier-only': 'Carrier only', 'current-only': 'Missing from carrier',
            },
            decisionLabels: {
                pending: 'Pending', 'no-action': 'No action', 'include-candidate': 'Include candidate',
                'exclude-candidate': 'Exclude candidate', 'needs-documentation': 'Needs documentation',
            },
            coverageLabels: {
                unknown: 'Unknown', covered: 'Covered', partially_covered: 'Partially covered',
                not_covered: 'Not covered', disputed: 'Disputed',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: insuranceClaimsCarrierReviewCopyValueSchema,
});
export const insuranceClaimsCompliancePaymentConfigurationValueSchema = z.strictObject({
    maximumComplianceSources: z.number().int().min(1).max(20),
    maximumReviewTermDays: z.number().int().min(1).max(3_650),
    requireApprovedComplianceForPaymentAdministration: z.boolean(),
    allowOperatorAttestedPaymentEvidence: z.boolean(),
});
export const insuranceClaimsCompliancePaymentConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.compliance-payment.configuration',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims compliance and payment-administration behavior',
        helpText: 'Controls bounded sourced reviews and administrative payment evidence. It does not supply legal language or authorize financial transactions.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            maximumComplianceSources: 20,
            maximumReviewTermDays: 1_095,
            requireApprovedComplianceForPaymentAdministration: false,
            allowOperatorAttestedPaymentEvidence: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'protected',
    },
    valueSchema: insuranceClaimsCompliancePaymentConfigurationValueSchema,
});
export const insuranceClaimsCompliancePaymentDisplayValueSchema = z.strictObject({
    defaultView: z.enum(['compliance', 'payment-administration']),
    cardSize: z.enum(['compact', 'standard', 'wide']),
    showRetiredComplianceReviews: z.boolean(),
    showClosedPaymentAdministrations: z.boolean(),
    showCompletedPaymentMilestones: z.boolean(),
});
export const insuranceClaimsCompliancePaymentDisplaySetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.compliance-payment.display',
        moduleId: 'insurance-claims',
        title: 'Insurance Claims compliance and payment-administration display',
        helpText: 'Controls the default view, card sizing, and completed-record visibility.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            defaultView: 'compliance', cardSize: 'standard',
            showRetiredComplianceReviews: false,
            showClosedPaymentAdministrations: true,
            showCompletedPaymentMilestones: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: true, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: insuranceClaimsCompliancePaymentDisplayValueSchema,
});
export const insuranceClaimsCompliancePaymentCopyValueSchema = z.strictObject({
    compliancePaymentTabLabel: label,
    complianceTabLabel: label,
    paymentAdministrationTabLabel: label,
    complianceTitle: label,
    complianceDescription: z.string().trim().min(1).max(300),
    paymentAdministrationTitle: label,
    paymentAdministrationDescription: z.string().trim().min(1).max(300),
    createComplianceReviewAction: label,
    reviseComplianceReviewAction: label,
    submitComplianceReviewAction: label,
    approveComplianceReviewAction: label,
    blockComplianceReviewAction: label,
    retireComplianceReviewAction: label,
    restoreComplianceReviewAction: label,
    createPaymentAdministrationAction: label,
    saveMilestoneAction: label,
    closePaymentAdministrationAction: label,
    reopenPaymentAdministrationAction: label,
    cancelAction: label,
    addSourceAction: label,
    removeSourceAction: label,
    jurisdictionStateLabel: label,
    jurisdictionNameLabel: label,
    guidanceModeLabel: label,
    sourceTitleLabel: label,
    sourceAuthorityLabel: label,
    sourceUrlLabel: label,
    sourceInternalReferenceLabel: label,
    sourceAccessedOnLabel: label,
    publicAdjusterBoundaryLabel: label,
    customerDisclaimerLabel: label,
    supplementLanguageLabel: label,
    deductibleGuardrailLabel: label,
    mortgageCheckGuardrailLabel: label,
    effectiveOnLabel: label,
    reviewDueOnLabel: label,
    claimLabel: label,
    currencyLabel: label,
    complianceReviewLabel: label,
    milestoneLabel: label,
    statusLabel: label,
    amountLabel: label,
    evidenceLabel: label,
    evidenceNoteLabel: label,
    carrierReviewIdLabel: label,
    reasonLabel: label,
    readinessLabel: label,
    noComplianceReviews: z.string().trim().min(1).max(300),
    noPaymentAdministrations: z.string().trim().min(1).max(300),
    loadingLabel: label,
    savedLabel: label,
    loadError: z.string().trim().min(1).max(300),
    mutationError: z.string().trim().min(1).max(300),
    permissionDenied: z.string().trim().min(1).max(300),
    attentionLabel: label,
    blockedLabel: label,
    paymentCompletionConnector: z.string().trim().min(1).max(80),
    complianceReadinessLabels: z.record(z.enum(['ready', 'not-ready', 'blocked']), label),
    complianceStateLabels: z.record(insuranceClaimsComplianceReviewStateSchema, label),
    guidanceModeLabels: z.record(insuranceClaimsComplianceGuidanceModeSchema, label),
    sourceAuthorityLabels: z.record(insuranceClaimsComplianceSourceAuthoritySchema, label),
    paymentAdministrationStateLabels: z.record(insuranceClaimsPaymentAdministrationStateSchema, label),
    paymentEvidenceKindLabels: z.record(insuranceClaimsPaymentEvidenceKindSchema, label),
    paymentMilestoneLabels: z.record(insuranceClaimsPaymentMilestoneKeySchema, label),
    paymentMilestoneStatusLabels: z.record(insuranceClaimsPaymentMilestoneStatusSchema, label),
});
export const insuranceClaimsCompliancePaymentCopySetting = definePlatformSetting({
    definition: {
        key: 'insurance-claims.copy.compliance-payment', moduleId: 'insurance-claims',
        title: 'Insurance Claims compliance and payment-administration copy',
        helpText: 'Controls ordinary interface labels only. Approved jurisdiction guidance remains a sourced, versioned protected record.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            compliancePaymentTabLabel: 'Compliance & payments',
            complianceTabLabel: 'Compliance reviews',
            paymentAdministrationTabLabel: 'Payment administration',
            complianceTitle: 'Jurisdiction compliance reviews',
            complianceDescription: 'Maintain sourced, versioned jurisdiction guidance with a separate approval decision.',
            paymentAdministrationTitle: 'Claim payment administration',
            paymentAdministrationDescription: 'Track deductible, ACV, RCV, recoverable depreciation, and mortgage-check milestones without moving money.',
            createComplianceReviewAction: 'Create compliance review',
            reviseComplianceReviewAction: 'Save review revision',
            submitComplianceReviewAction: 'Submit for review',
            approveComplianceReviewAction: 'Approve sourced review',
            blockComplianceReviewAction: 'Block review',
            retireComplianceReviewAction: 'Retire review',
            restoreComplianceReviewAction: 'Restore as draft',
            createPaymentAdministrationAction: 'Create payment administration',
            saveMilestoneAction: 'Save milestone',
            closePaymentAdministrationAction: 'Close administration',
            reopenPaymentAdministrationAction: 'Reopen administration',
            cancelAction: 'Cancel', addSourceAction: 'Add source', removeSourceAction: 'Remove source',
            jurisdictionStateLabel: 'State', jurisdictionNameLabel: 'Jurisdiction name',
            guidanceModeLabel: 'Guidance mode', sourceTitleLabel: 'Source title',
            sourceAuthorityLabel: 'Source authority', sourceUrlLabel: 'Source URL',
            sourceInternalReferenceLabel: 'Internal source reference',
            sourceAccessedOnLabel: 'Source accessed on',
            publicAdjusterBoundaryLabel: 'Public-adjuster boundary summary',
            customerDisclaimerLabel: 'Customer disclaimer',
            supplementLanguageLabel: 'Supplement language notes',
            deductibleGuardrailLabel: 'Deductible guardrail',
            mortgageCheckGuardrailLabel: 'Mortgage-check guardrail',
            effectiveOnLabel: 'Effective on', reviewDueOnLabel: 'Review due on',
            claimLabel: 'Claim', currencyLabel: 'Currency', complianceReviewLabel: 'Compliance review',
            milestoneLabel: 'Milestone', statusLabel: 'Status', amountLabel: 'Amount',
            evidenceLabel: 'Evidence source', evidenceNoteLabel: 'Evidence explanation',
            carrierReviewIdLabel: 'Carrier review ID',
            reasonLabel: 'Reason', readinessLabel: 'Readiness',
            noComplianceReviews: 'No jurisdiction compliance reviews match this view.',
            noPaymentAdministrations: 'No claim payment-administration records match this view.',
            loadingLabel: 'Loading compliance and payment administration…',
            savedLabel: 'Compliance and payment-administration change saved.',
            loadError: 'Compliance and payment administration could not be loaded.',
            mutationError: 'The compliance or payment-administration change could not be saved.',
            permissionDenied: 'You do not have permission to make this change.',
            attentionLabel: 'Attention', blockedLabel: 'Blocked',
            paymentCompletionConnector: 'of 5 complete',
            complianceReadinessLabels: {
                ready: 'Ready', 'not-ready': 'Not ready', blocked: 'Blocked',
            },
            complianceStateLabels: {
                draft: 'Draft', 'needs-review': 'Needs review', approved: 'Approved',
                blocked: 'Blocked', retired: 'Retired',
            },
            guidanceModeLabels: {
                'contractor-documentation': 'Contractor documentation',
                'referral-required': 'Referral required', disabled: 'Disabled',
            },
            sourceAuthorityLabels: {
                official: 'Official', counsel: 'Counsel', 'internal-policy': 'Internal policy',
            },
            paymentAdministrationStateLabels: { active: 'Active', closed: 'Closed' },
            paymentEvidenceKindLabels: {
                'system-initialized': 'System initialized', 'operator-attested': 'Operator attested',
                'claim-snapshot': 'Claim snapshot', 'carrier-review': 'Carrier review',
            },
            paymentMilestoneLabels: {
                deductible: 'Deductible', acv: 'ACV', rcv: 'RCV',
                'recoverable-depreciation': 'Recoverable depreciation',
                'mortgage-check': 'Mortgage check',
            },
            paymentMilestoneStatusLabels: {
                unknown: 'Unknown', 'not-applicable': 'Not applicable', pending: 'Pending',
                documented: 'Documented', collected: 'Collected', received: 'Received',
                requested: 'Requested', 'not-ready': 'Not ready', denied: 'Denied',
                endorsed: 'Endorsed', deposited: 'Deposited', released: 'Released',
                reconciled: 'Reconciled', blocked: 'Blocked',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: true, safetyClass: 'ordinary',
    },
    valueSchema: insuranceClaimsCompliancePaymentCopyValueSchema,
});
export const insuranceClaimsSettingRegistrations = [
    insuranceClaimsConfigurationSetting,
    insuranceClaimsDisplaySetting,
    insuranceClaimsCopySetting,
    insuranceClaimsWorkflowConfigurationSetting,
    insuranceClaimsWorkflowDisplaySetting,
    insuranceClaimsWorkflowCopySetting,
    insuranceClaimsNoticeSlaConfigurationSetting,
    insuranceClaimsNoticeSlaDisplaySetting,
    insuranceClaimsNoticeSlaCopySetting,
    insuranceClaimsCarrierReviewConfigurationSetting,
    insuranceClaimsCarrierReviewDisplaySetting,
    insuranceClaimsCarrierReviewCopySetting,
    insuranceClaimsCompliancePaymentConfigurationSetting,
    insuranceClaimsCompliancePaymentDisplaySetting,
    insuranceClaimsCompliancePaymentCopySetting,
];
//# sourceMappingURL=insurance-claims-setting-definitions.js.map